import {
  VOUCHER_HEADER_COLOR_PATTERN,
  VOUCHER_IDENTITY_EMAIL_PATTERN,
  VOUCHER_IDENTITY_LIMITS,
} from '@bandido/shared/types';

const KEY = 'vouchers.config';

/**
 * LA LÓGICA de la pantalla de identidad, fuera del componente y sin DOM: qué
 * campos hay, qué es un borrador cambiado, qué cuerpo se manda y por qué
 * Guardar puede estar apagado. El componente cablea; acá se decide, y acá se
 * prueba.
 */

/** Los campos de texto, en el orden en que se muestran. */
export const VOUCHER_IDENTITY_FIELDS = [
  'legalName',
  'taxId',
  'phone',
  'email',
  'website',
  'address',
  'assistancePhone',
] as const;

export type VoucherIdentityField = (typeof VOUCHER_IDENTITY_FIELDS)[number];

export interface VoucherIdentityDraft extends Record<VoucherIdentityField, string> {
  headerColor: string;
}

/** El tope de cada campo sale del DTO, no de una copia. */
export function voucherIdentityMaxLength(field: VoucherIdentityField): number {
  return VOUCHER_IDENTITY_LIMITS[field];
}

/**
 * El estado guardado, al borrador del formulario. Los campos ausentes son
 * cadenas vacías: un `undefined` en un input lo vuelve no controlado y React
 * pierde lo que el usuario escribió.
 */
export function voucherIdentityDraft(identity: Partial<VoucherIdentityDraft> | undefined): VoucherIdentityDraft {
  const draft = {
    headerColor: identity?.headerColor ?? '',
  } as VoucherIdentityDraft;
  for (const field of VOUCHER_IDENTITY_FIELDS) {
    draft[field] = identity?.[field] ?? '';
  }
  return draft;
}

/** ¿Hay algo sin guardar? Lo que decide el aviso al navegar y el botón. */
export function voucherIdentityChanged(draft: VoucherIdentityDraft, saved: VoucherIdentityDraft): boolean {
  return (
    draft.headerColor !== saved.headerColor || VOUCHER_IDENTITY_FIELDS.some((field) => draft[field] !== saved[field])
  );
}

/**
 * Qué está mal, por campo, con las MISMAS formas que valida el backend
 * (`voucher-config.dto.ts`). Un vacío siempre vale: es "borralo".
 */
/** El borrador con los espacios recortados: lo que de verdad viaja. */
export function voucherIdentityTrimmed(draft: VoucherIdentityDraft): VoucherIdentityDraft {
  const limpio = {
    headerColor: draft.headerColor.trim(),
  } as VoucherIdentityDraft;
  for (const field of VOUCHER_IDENTITY_FIELDS) {
    limpio[field] = draft[field].trim();
  }
  return limpio;
}

export function voucherIdentityIssues(
  draft: VoucherIdentityDraft,
): Partial<Record<keyof VoucherIdentityDraft, string>> {
  // Se juzga lo que SE VA A MANDAR, no lo que está escrito: el cuerpo recorta,
  // así que validando el texto crudo un mail pegado con espacios alrededor
  // —la forma normal de llenar esto— se marcaba inválido y bloqueaba el
  // Guardar, cuando el valor que iba a viajar era perfecto.
  const limpio = voucherIdentityTrimmed(draft);
  const issues: Partial<Record<keyof VoucherIdentityDraft, string>> = {};
  for (const field of VOUCHER_IDENTITY_FIELDS) {
    if (limpio[field].length > voucherIdentityMaxLength(field)) {
      issues[field] = `${KEY}.identity.tooLong`;
    }
  }
  if (!VOUCHER_IDENTITY_EMAIL_PATTERN.test(limpio.email)) {
    issues.email = `${KEY}.identity.emailInvalid`;
  }
  if (!VOUCHER_HEADER_COLOR_PATTERN.test(limpio.headerColor)) {
    issues.headerColor = `${KEY}.identity.colorInvalid`;
  }
  return issues;
}

/**
 * LA MITAD de identidad del cuerpo del guardado (los textos los arma
 * `voucherTexts.ts`). Una sola regla: viaja únicamente lo que se TOCÓ.
 *
 * De `identity`, SOLO los campos que este asesor cambió respecto de lo que
 * cargó. El backend está hecho para eso: `identityWrite` recorre las claves
 * PRESENTES, así que una clave ausente queda intacta y una presente con vacío
 * se borra. Mandando los ocho siempre, dos administradores editando campos
 * distintos se pisaban: el que guardaba segundo devolvía el teléfono de
 * asistencia al valor que había leído al abrir la pantalla, y eso sale impreso
 * en los documentos del pasajero.
 */
export function voucherIdentityPayload(
  draft: VoucherIdentityDraft,
  base: VoucherIdentityDraft,
): { identity: Partial<VoucherIdentityDraft> } {
  const limpio = voucherIdentityTrimmed(draft);
  const previo = voucherIdentityTrimmed(base);
  const identity: Partial<VoucherIdentityDraft> = {};
  for (const field of [...VOUCHER_IDENTITY_FIELDS, 'headerColor'] as const) {
    // Vaciar un campo ES un cambio, y viaja como cadena vacía: así se borra.
    if (limpio[field] !== previo[field]) identity[field] = limpio[field];
  }
  return { identity };
}

/**
 * Por qué Guardar está apagado, para decirlo en vez de dejar un botón muerto.
 * El orden importa: sin permiso va primero, porque explica todo lo demás.
 */
export function voucherSaveDisabledReason(context: {
  loaded: boolean;
  canEdit: boolean;
  changed: boolean;
  saving: boolean;
  issues: Partial<Record<keyof VoucherIdentityDraft, string>>;
  /** Lo que está mal en los TEXTOS, que apaga el mismo botón. */
  textIssues?: Record<string, unknown>;
}): string | null {
  if (!context.canEdit) return `${KEY}.save.needsAdmin`;
  /*
   * Lo que NO se leyó no se puede escribir. El guardado manda los ocho campos
   * siempre —vacío significa "borralo", que es como se limpia uno—, así que
   * con la configuración sin cargar (el GET falló, o todavía no volvió) un
   * Guardar dejaba en NULL el RUT, los dos teléfonos, el mail, el sitio, la
   * dirección y el color de TODA la agencia, con un toast verde. Y esos datos
   * no son solo de esta pantalla: el motor los imprime en cada documento.
   */
  if (!context.loaded) return `${KEY}.save.notLoaded`;
  if (context.saving) return `${KEY}.save.saving`;
  const problemas =
    Object.keys(context.issues).length +
    Object.keys(context.textIssues ?? {}).length;
  if (problemas > 0) return `${KEY}.save.hasIssues`;
  if (!context.changed) return `${KEY}.save.noChanges`;
  return null;
}

/**
 * El color de cabecera tiene TRES estados y el selector nativo solo sabe dos:
 * no hay color (cabecera lisa, con el logo normal), hay color (banda de ese
 * color, con el logo NEGATIVO), y el blanco es un color como cualquier otro.
 *
 * El motor decide con `banded = Boolean(headerColor && negative)`
 * (`voucher-document.ts`): con `#ffffff` imprime el logo negativo —el que está
 * hecho para fondos oscuros— sobre una banda blanca, y el documento sale sin
 * marca visible. Como `<input type="color">` no puede representar "sin color"
 * y muestra blanco, hace falta un interruptor aparte: sin él, abrir el
 * selector y aceptar convertía "sin color" en "blanco" sin que nadie lo note.
 */
export const VOUCHER_HEADER_COLOR_DEFAULT = '#1c2b4a';

export function voucherHeaderColorEnabled(headerColor: string): boolean {
  return headerColor.trim().length > 0;
}

/** Prender o apagar el color, conservando el último elegido mientras dure. */
export function voucherToggleHeaderColor(headerColor: string, enabled: boolean, last: string): string {
  if (!enabled) return '';
  if (voucherHeaderColorEnabled(headerColor)) return headerColor;
  return voucherHeaderColorEnabled(last) ? last : VOUCHER_HEADER_COLOR_DEFAULT;
}

/**
 * El mensaje de un logo rechazado: el del BACKEND cuando lo manda, porque es
 * el que dice qué pasó de verdad —el formato sale de los bytes, no de lo que
 * declaró el navegador— y traducirlo acá sería adivinar.
 */
export function voucherLogoError(error: unknown): {
  message?: string;
  key: string;
} {
  const response = (
    error as {
      response?: { status?: number; data?: { message?: unknown } };
    } | null
  )?.response;
  /*
   * El 413 lo corta el interceptor de subida ANTES del servicio, así que su
   * mensaje es el de multer: un `File too large` en inglés, en una pantalla
   * que está en español. Ese caso lleva texto propio.
   */
  if (response?.status === 413) return { key: `${KEY}.logo.tooBig` };
  const message = response?.data?.message;
  if (typeof message === 'string' && message.length > 0) {
    return { message, key: `${KEY}.logo.rejected` };
  }
  return { key: `${KEY}.logo.failed` };
}

/**
 * QUÉ HACER cuando llega configuración nueva del servidor.
 *
 * El borrador se compara contra su BASE —de dónde salió—, no contra lo último
 * que trajo el servidor, y esa distinción es todo:
 *
 * - Si el asesor no tocó nada, el borrador adopta lo nuevo y la base se mueve
 *   con él. Sin esto, un refetch —react-query repite al volver a la pestaña—
 *   traía la identidad que otro administrador acababa de cambiar, el borrador
 *   viejo pasaba a figurar como "cambios sin guardar", y un Guardar le
 *   revertía el trabajo al otro.
 * - Si tocó algo, no se le pisa: sus ediciones valen más que un refresco.
 *
 * Devuelve `null` cuando no hay nada que hacer.
 */
export function voucherDraftSync(params: {
  draft: VoucherIdentityDraft;
  base: VoucherIdentityDraft;
  incoming: VoucherIdentityDraft;
}): { draft: VoucherIdentityDraft; base: VoucherIdentityDraft } | null {
  if (voucherIdentityChanged(params.draft, params.base)) return null;
  if (!voucherIdentityChanged(params.incoming, params.base)) return null;
  return { draft: params.incoming, base: params.incoming };
}
